import React, { useMemo, useState } from "react";
import { Sprout, TrendingUp, Award } from "lucide-react";

const BOX_SIZES = [
  { key: "small", label: "Small", price: 65 },
  { key: "medium", label: "Medium", price: 90 },
  { key: "large", label: "Large", price: 120 },
];

const FREQUENCIES = [
  { key: "weekly", label: "Weekly", deliveries: 12 },
  { key: "fortnightly", label: "Fortnightly", deliveries: 6 },
  { key: "monthly", label: "Monthly", deliveries: 3 },
];

const BONUS_TIERS = [
  { subs: 25, bonus: 1500 },
  { subs: 50, bonus: 3500 },
  { subs: 75, bonus: 6000 },
  { subs: 100, bonus: 10000 },
];

export default function RevenueCalculator() {
  const [subs, setSubs] = useState(25);
  const [boxKey, setBoxKey] = useState("medium");
  const [freqKey, setFreqKey] = useState("weekly");
  const [year, setYear] = useState(1);

  const box = BOX_SIZES.find((b) => b.key === boxKey);
  const freq = FREQUENCIES.find((f) => f.key === freqKey);
  const split = year === 1 ? 0.6 : 0.7;

  const numbers = useMemo(() => {
    const seasonValuePerSub = box.price * freq.deliveries; // $ over 12-week season
    const grossPerDelivery = subs * box.price;
    const grossPerSeason = grossPerDelivery * freq.deliveries;
    const growerPerDelivery = grossPerDelivery * split;
    const growerPerSeason = grossPerSeason * split;
    const heritagePerSeason = grossPerSeason * (1 - split);
    const bonus = BONUS_TIERS.reduce((acc, t) => (subs >= t.subs ? t.bonus : acc), 0);
    return {
      seasonValuePerSub,
      grossPerDelivery,
      growerPerDelivery,
      growerPerSeason,
      heritagePerSeason,
      bonus,
      totalWithBonus: growerPerSeason + bonus,
    };
  }, [subs, box, freq, split]);

  const fmt = (n) => `$${n.toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;

  return (
    <div className="bloom-card p-8 md:p-10" data-testid="revenue-calculator">
      <div className="flex items-center gap-3 mb-3">
        <Sprout className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl">Revenue Calculator</h3>
      </div>
      <p className="text-sm text-[var(--bloom-ink-soft)] mb-10 max-w-xl">
        Model your earnings across a 12-week Summer Bloom Season. Choose a box size, delivery
        frequency, and your subscriber count. Year One pays 60/40; from Year Two onwards
        Founding Growers move to 70/30 with the same bonus tiers applied at season end.
      </p>

      <div className="grid md:grid-cols-2 gap-12">
        {/* Inputs */}
        <div className="space-y-8">
          {/* Box size */}
          <div>
            <span className="bloom-label block mb-3">Bloom Box Size</span>
            <div className="grid grid-cols-3 border border-[var(--bloom-border)]">
              {BOX_SIZES.map((b, i) => (
                <button
                  key={b.key}
                  onClick={() => setBoxKey(b.key)}
                  className={`px-3 py-3 text-center transition ${i > 0 ? "border-l border-[var(--bloom-border)]" : ""} ${boxKey === b.key ? "bg-[var(--bloom-ink)] text-[var(--bloom-bg)]" : "text-[var(--bloom-ink-soft)]"}`}
                  data-testid={`calc-box-${b.key}`}
                >
                  <div className="text-[10px] tracking-[0.15em] uppercase">{b.label}</div>
                  <div className="font-serif text-xl mt-1">${b.price}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Frequency */}
          <div>
            <span className="bloom-label block mb-3">Delivery Frequency</span>
            <div className="grid grid-cols-3 border border-[var(--bloom-border)]">
              {FREQUENCIES.map((f, i) => (
                <button
                  key={f.key}
                  onClick={() => setFreqKey(f.key)}
                  className={`px-3 py-3 text-center transition ${i > 0 ? "border-l border-[var(--bloom-border)]" : ""} ${freqKey === f.key ? "bg-[var(--bloom-ink)] text-[var(--bloom-bg)]" : "text-[var(--bloom-ink-soft)]"}`}
                  data-testid={`calc-freq-${f.key}`}
                >
                  <div className="text-[10px] tracking-[0.15em] uppercase">{f.label}</div>
                  <div className="font-serif text-base mt-1">{f.deliveries}× / season</div>
                </button>
              ))}
            </div>
          </div>

          {/* Subscribers */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <span className="bloom-label">Subscribers</span>
              <span className="font-serif text-4xl" data-testid="calc-sub-count">{subs}</span>
            </div>
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              value={subs}
              onChange={(e) => setSubs(parseInt(e.target.value, 10))}
              className="bloom-slider"
              data-testid="calc-sub-slider"
            />
            <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-2">
              <span>10</span><span>25</span><span>50</span><span>75</span><span>100</span>
            </div>
          </div>

          {/* Year */}
          <div>
            <span className="bloom-label block mb-2">Revenue Year</span>
            <div className="inline-flex border border-[var(--bloom-border)]">
              <button
                onClick={() => setYear(1)}
                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase transition ${year === 1 ? "bg-[var(--bloom-ink)] text-[var(--bloom-bg)]" : "text-[var(--bloom-ink-soft)]"}`}
                data-testid="calc-year-1-btn"
              >
                Year 1 · 60/40
              </button>
              <button
                onClick={() => setYear(2)}
                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border-l border-[var(--bloom-border)] transition ${year === 2 ? "bg-[var(--bloom-ink)] text-[var(--bloom-bg)]" : "text-[var(--bloom-ink-soft)]"}`}
                data-testid="calc-year-2-btn"
              >
                Year 2+ · 70/30
              </button>
            </div>
          </div>

          {/* Bonus tiers */}
          <div>
            <span className="bloom-label block mb-3">Performance Bonus Tiers</span>
            <div className="space-y-1.5 text-xs">
              {BONUS_TIERS.map((t) => {
                const hit = subs >= t.subs;
                return (
                  <div key={t.subs} className={`flex items-center gap-3 ${hit ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-ink-muted)]"}`}>
                    <Award size={12} strokeWidth={1.5} className={hit ? "" : "opacity-40"} />
                    <span className="flex-1">{t.subs}+ active subscribers</span>
                    <span className="font-serif text-base">{fmt(t.bonus)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Output */}
        <div className="space-y-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)]">
            12-Week Summer Bloom Season
          </div>
          <Row label="Season value / subscriber" value={fmt(numbers.seasonValuePerSub)} subtle />
          <Row label="Gross per delivery" value={fmt(numbers.grossPerDelivery)} subtle />
          <Row label="Your share per delivery" value={fmt(numbers.growerPerDelivery)} />
          <Row label="Subscription revenue / season" value={fmt(numbers.growerPerSeason)} />
          <Row label="Performance bonus" value={numbers.bonus > 0 ? `+ ${fmt(numbers.bonus)}` : "·"} subtle={numbers.bonus === 0} />
          <div className="border-t border-[var(--bloom-border)] pt-6">
            <Row label="Estimated season total" value={fmt(numbers.totalWithBonus)} hero testid="calc-season-total" />
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--bloom-ink-muted)] mt-4">
            <TrendingUp size={12} />
            Heritage retains {fmt(numbers.heritagePerSeason)} (marketing, platform, subscriber acquisition).
          </div>
        </div>
      </div>

      <p className="text-[11px] text-[var(--bloom-ink-muted)] mt-8 italic font-serif">
        Estimate only. Assumes a single box-size and frequency across all subscribers.
        Real-world revenue will reflect your actual subscriber mix, retention rates and
        seasonal availability. Bonus paid in cash at season end against active subscriber count.
      </p>
    </div>
  );
}

function Row({ label, value, hero, subtle, testid }) {
  return (
    <div className="flex items-baseline justify-between" data-testid={testid}>
      <span className={`bloom-label ${subtle ? "opacity-70" : ""}`}>{label}</span>
      <span className={`font-serif ${hero ? "text-4xl text-[var(--bloom-brand)]" : "text-2xl text-[var(--bloom-ink)]"} ${subtle ? "opacity-70" : ""}`}>
        {value}
      </span>
    </div>
  );
}
